/** @jsx jsx */
import { BannerProps } from './types';
import { css, jsx } from '@emotion/react';
import type { ResponseData } from '../useAlertBanner/useAlertBannerData';
import { labelStyles } from '@zapier/style-encapsulation';
import { Alert, Icon, IconName, Link, Typography } from '@zapier/design-system';
import {
  formatResponseDataToTaskBannerEligibility,
  getTaskAlertBannerEligibility,
  TASK_ALERT_BANNERS,
} from './utils';
import { hasDismissedBannerBetweenDates } from './utils/hasDismissedBannerBetweenDates';
import { useUniversalNavContext } from '../../../../universal-nav/hooks';
import { emitFullStoryEvent } from '../../../../universal-nav/utils/fullStoryEvents';
import { UserInteractionEvent } from '../../../../universal-nav/utils/avroEvents';
import { Fragment, MouseEvent, useEffect, useState } from 'react';

export const shouldRender = (data: ResponseData) => {
  const { currentAccount } = data;

  const isOwnerOrAdminOrSuperAdmin =
    currentAccount?.currentUserRole &&
    ['owner', 'admin', 'super_admin'].includes(currentAccount?.currentUserRole);

  if (!isOwnerOrAdminOrSuperAdmin) return false;

  const isUnlimitedAccount = data.currentAccount?.isUnlimited;

  if (isUnlimitedAccount) return false;

  const periodStartDate = currentAccount.periodStartDate;
  const periodEndDate = currentAccount.periodEndDate;

  const banner = getTaskAlertBannerEligibility(
    formatResponseDataToTaskBannerEligibility(data)
  );

  if (!banner) {
    return false;
  }

  const hasDismissedTargetBannerThisPeriod = hasDismissedBannerBetweenDates({
    dismissedOn: localStorage.getItem(TASK_ALERT_BANNERS[banner].dismissalKey),
    startDate: periodStartDate,
    endDate: periodEndDate,
  });

  return !hasDismissedTargetBannerThisPeriod;
};

const Styles = labelStyles('TaskUsageAlertBanner', {
  icon: css({
    display: 'inline-block',
    verticalAlign: 'middle',
    marginRight: '10px',
    position: 'relative',
  }),
  strong: css({
    font: `var(--zds-body-4, 700 14px/24px ${Typography.base.fontFamily})`,
  }),
});

const useTaskUsageBanner = (props: BannerProps) => {
  const { getFullyQualifiedURL } = useUniversalNavContext();

  const banner = getTaskAlertBannerEligibility(
    formatResponseDataToTaskBannerEligibility(props.data)
  );

  const [dismissedOn, setDismissedOn] = useState<string | null>(
    banner
      ? localStorage.getItem(TASK_ALERT_BANNERS[banner].dismissalKey)
      : null
  );

  const periodStartDate = props.data.currentAccount.periodStartDate;
  const periodEndDate = props.data.currentAccount.periodEndDate;

  const hasDismissedTargetBannerThisPeriod = hasDismissedBannerBetweenDates({
    dismissedOn,
    startDate: periodStartDate,
    endDate: periodEndDate,
  });

  useEffect(() => {
    if (!banner || hasDismissedTargetBannerThisPeriod) return;

    const eventOptions = {
      interaction_name: 'task_alert_banner_rendered',
      event_action: 'in_view',
      interaction_goal: 'not_applicable',
      option_selected: 'not_applicable',
    };

    props.trackEvent(UserInteractionEvent, eventOptions);

    emitFullStoryEvent(eventOptions);
  }, [hasDismissedTargetBannerThisPeriod, banner]);

  useEffect(() => {
    if (!banner || dismissedOn === null) return;

    // Wrap localStorage write in try/catch in case browser memory quota is exceeded
    try {
      localStorage.setItem(
        TASK_ALERT_BANNERS[banner].dismissalKey,
        JSON.stringify(dismissedOn)
      );
    } catch {
      console.log('Failed to save task alert banner dismissal to localStorage');
    }
  }, [banner, dismissedOn]);

  if (hasDismissedTargetBannerThisPeriod || !banner) {
    return {
      component: null,
    };
  }

  const onDismiss = () => {
    setDismissedOn(new Date().toUTCString());

    const eventOptions = {
      interaction_name: 'task_alert_banner_dismissed',
      event_action: 'click',
      interaction_goal: 'dismiss_banner',
      option_selected: 'not_applicable',
    };

    props.trackEvent(UserInteractionEvent, eventOptions);

    emitFullStoryEvent(eventOptions);
  };

  const upgradeURL = getFullyQualifiedURL(
    props.data.nextPlan?.id
      ? `/app/planbuilder/plans?planId=${props.data.nextPlan?.id}`
      : `/app/planbuilder/plans`
  );

  const billingSettingsURL = getFullyQualifiedURL(
    encodeURI(`/app/settings/${props.data.currentAccount.id}/billing`)
  );

  const contactSalesURL =
    'https://zapier.com/l/contact-sales?demo_source=EntSKU-logged-in_task-limit-alert';

  const onLinkClick = (e: MouseEvent, interactionGoal: string) => {
    const eventOptions = {
      interaction_name: 'task_alert_banner_cta_clicked',
      interaction_raw_text: e.currentTarget?.textContent ?? '',
      destination_url_address: e.currentTarget.getAttribute('href') ?? '',
      interaction_goal: interactionGoal,
      option_selected: 'not_applicable',
      event_action: 'click',
    };

    props.trackEvent(UserInteractionEvent, eventOptions);

    emitFullStoryEvent(eventOptions);
  };

  const upgradeLink = (text: string) => (
    <Link href={upgradeURL} onClick={(e) => onLinkClick(e, 'upgrade_plan')}>
      {text}
    </Link>
  );

  const manageLink = (text: string) => (
    <Link
      href={billingSettingsURL}
      onClick={(e) => onLinkClick(e, 'manage_plan')}
    >
      {text}
    </Link>
  );

  const contactSalesLink = (text: string) => (
    <Link
      href={contactSalesURL}
      onClick={(e) => onLinkClick(e, 'contact_sales')}
    >
      {text}
    </Link>
  );

  const alertText = (
    primaryText: string,
    secondaryText: string,
    iconName: IconName
  ) => (
    <Fragment>
      <strong css={Styles.strong}>
        <span css={Styles.icon}>
          <Icon name={iconName} size={24} />
        </span>
        {primaryText}
      </strong>
      {secondaryText}
    </Fragment>
  );

  const banners = {
    freeAccount: {
      [TASK_ALERT_BANNERS.lowTasks.banner]: (
        <Alert layout={props.layout} type="warning" onDismiss={onDismiss}>
          <p data-zds>
            {alertText(
              "You're running low on tasks. ",
              'To prevent your Zaps from being paused, ',
              'alertTriangleFill'
            )}
            {upgradeLink('update your plan now.')}
          </p>
        </Alert>
      ),
      [TASK_ALERT_BANNERS.outOfTasks.banner]: (
        <Alert layout={props.layout} type="error" onDismiss={onDismiss}>
          <p data-zds css={Styles.text}>
            {alertText(
              'Your Zaps are paused. ',
              'To turn them back on, upgrade your plan to ',
              'alertTriangleFill'
            )}
            {upgradeLink('get more tasks.')}
          </p>
        </Alert>
      ),
    },
    individualAccount: {
      [TASK_ALERT_BANNERS.lowTasks.banner]: (
        <Alert layout={props.layout} type="warning" onDismiss={onDismiss}>
          <p data-zds css={Styles.text}>
            {alertText(
              "You're running low on tasks. ",
              'To prevent your Zaps from being paused, ',
              'alertTriangleFill'
            )}
            {upgradeLink('get more tasks.')}
          </p>
        </Alert>
      ),
      [TASK_ALERT_BANNERS.outOfTasks.banner]: (
        <Alert layout={props.layout} type="error" onDismiss={onDismiss}>
          <p data-zds css={Styles.text}>
            {alertText(
              'Your Zaps are paused. ',
              'To turn them back on, ',
              'alertTriangleFill'
            )}
            {upgradeLink('get more tasks.')}
          </p>
        </Alert>
      ),
      [TASK_ALERT_BANNERS.aboutToEnterMeteredBilling.banner]: (
        <Alert layout={props.layout} type="info" onDismiss={onDismiss}>
          <p data-zds css={Styles.text}>
            {alertText(
              "You're nearing your plan's task limit. ",
              'Pay-per-task billing will activate so your Zaps keep running or you can ',
              'alertInfoFill'
            )}
            {manageLink('manage your plan.')}
          </p>
        </Alert>
      ),
      [TASK_ALERT_BANNERS.enteredMeteredBilling.banner]: (
        <Alert layout={props.layout} type="info" onDismiss={onDismiss}>
          <p data-zds css={Styles.text}>
            {alertText(
              "You've reached your plan's task limit. ",
              'Pay-per-task billing will keep your Zaps running or you can ',
              'alertInfoFill'
            )}
            {manageLink('manage your plan.')}
          </p>
        </Alert>
      ),
    },
    teamAccount: {
      [TASK_ALERT_BANNERS.lowTasks.banner]: (
        <Alert layout={props.layout} type="warning" onDismiss={onDismiss}>
          <p data-zds css={Styles.text}>
            {alertText(
              'Your account is running low on tasks. ',
              'To prevent your Zaps from being paused, ',
              'alertTriangleFill'
            )}
            {upgradeLink('get more tasks.')}
          </p>
        </Alert>
      ),
      [TASK_ALERT_BANNERS.outOfTasks.banner]: (
        <Alert layout={props.layout} type="error" onDismiss={onDismiss}>
          <p data-zds css={Styles.text}>
            {alertText(
              "Your Account's Zaps are paused. ",
              'To turn them back on, ',
              'alertTriangleFill'
            )}
            {upgradeLink('get more tasks.')}
          </p>
        </Alert>
      ),
      [TASK_ALERT_BANNERS.aboutToEnterMeteredBilling.banner]: (
        <Alert layout={props.layout} type="info" onDismiss={onDismiss}>
          <p data-zds css={Styles.text}>
            {alertText(
              "You're nearing your plan's task limit. ",
              'Pay-per-task billing will activate so your Zaps keep running or you can ',
              'alertInfoFill'
            )}
            {manageLink('manage your plan.')}
          </p>
        </Alert>
      ),
      [TASK_ALERT_BANNERS.enteredMeteredBilling.banner]: (
        <Alert layout={props.layout} type="info" onDismiss={onDismiss}>
          <p data-zds css={Styles.text}>
            {alertText(
              "You've reached your plan's task limit. ",
              'Pay-per-task billing will keep your Zaps running or you can ',
              'alertInfoFill'
            )}
            {manageLink('manage your plan.')}
          </p>
        </Alert>
      ),
    },
    salesManagedAccount: {
      [TASK_ALERT_BANNERS.lowTasks.banner]: (
        <Alert layout={props.layout} type="warning" onDismiss={onDismiss}>
          <p data-zds css={Styles.text}>
            {alertText(
              'Your account is running low on tasks. ',
              'To prevent your Zaps from being paused, please ',
              'alertTriangleFill'
            )}
            {contactSalesLink('contact sales.')}
          </p>
        </Alert>
      ),
      [TASK_ALERT_BANNERS.outOfTasks.banner]: (
        <Alert layout={props.layout} type="error" onDismiss={onDismiss}>
          <p data-zds css={Styles.text}>
            {alertText(
              "Your Account's Zaps are paused. ",
              'To turn them back on, please ',
              'alertTriangleFill'
            )}
            {contactSalesLink('contact sales.')}
          </p>
        </Alert>
      ),
      [TASK_ALERT_BANNERS.aboutToEnterMeteredBilling.banner]: (
        <Alert layout={props.layout} type="info" onDismiss={onDismiss}>
          <p data-zds css={Styles.text}>
            {alertText(
              "You're nearing your plan's task limit. ",
              'Pay-per-task billing will activate so your Zaps keep running. ',
              'alertInfoFill'
            )}
            {contactSalesLink('Contact sales')} to manage your plan.
          </p>
        </Alert>
      ),
      [TASK_ALERT_BANNERS.enteredMeteredBilling.banner]: (
        <Alert layout={props.layout} type="info" onDismiss={onDismiss}>
          <p data-zds css={Styles.text}>
            {alertText(
              "You've reached your plan's task limit. ",
              'Pay-per-task billing will keep your Zaps running or to manage your plan, ',
              'alertInfoFill'
            )}
            {contactSalesLink('contact sales.')}
          </p>
        </Alert>
      ),
    },
  } as const;

  if (props.data.currentAccount.plan?.limits?.isSelfServe === false) {
    return {
      component: banners.salesManagedAccount[banner] ?? null,
    };
  }

  if (props.data.currentAccount.isTeamAccount) {
    return {
      component: banners.teamAccount[banner] ?? null,
    };
  }

  if (props.data.currentAccount.plan.amount === 0) {
    return {
      component: banners.freeAccount[banner] ?? null,
    };
  }

  return {
    component: banners.individualAccount[banner] ?? null,
  };
};

export const Component = function TaskUsage(props: BannerProps) {
  const { component } = useTaskUsageBanner(props);

  if (!component) return null;

  return component;
};
